import { ExternalLinkIcon } from "icons/outline";
import { isJsonString } from "../../features/form/utils/isJsonString";
import { isString } from "../../features/form/utils/isString";
import { isXmlString } from "../../features/form/utils/isXmlString";
import { CodeEditor } from "../../features/sandbox/components/CodeEditor";
import { snakeCaseToHumanReadable } from "../../utils/snakeCaseToHumanReadable";
import BooleanBadge from "../BooleanBadge";
import CopyIconButton from "../CopyIconButton";
import { MaskedText } from "./MaskedText";

const JsonPreview = ({
  itemValue,
}: {
  itemValue: string | Record<string, string>;
}) => (
  <div className="text-sm font-normal text-foundations-bolder">
    <pre className="whitespace-normal break-words rounded border border-foundations-subtle bg-foundations-subtelst-02 p-2 text-foundations-bold">
      {isJsonString(itemValue)
        ? JSON.stringify(JSON.parse(itemValue))
        : typeof itemValue === "object" &&
            itemValue !== null &&
            !isXmlString(itemValue)
          ? JSON.stringify(itemValue)
          : itemValue}
    </pre>
  </div>
);

export function SidePanelPreviewItem({
  itemKey,
  itemValue,
}: {
  itemKey: string;
  itemValue: unknown;
}) {
  if (
    ["access_token", "key", "token", "public_key"].includes(itemKey) &&
    isString(itemValue)
  ) {
    // Don't use 'key' as a prop here
    return <MaskedText textKey={itemKey} textValue={itemValue} />;
  }

  if (itemValue && isJsonString(itemValue)) {
    return (
      <>
        <span className="flex justify-between text-sm font-normal capitalize text-foundations-subtle">
          {snakeCaseToHumanReadable(itemKey)}
        </span>
        <CodeEditor
          readOnly
          value={JSON.stringify(JSON.parse(itemValue), null, 2)}
          className="mt-1 [&_.textAreaCover]:bg-transparent"
          toolbarClassName="bg-foundations-subtlest"
        />
      </>
    );
  }

  if (itemValue && (isJsonString(itemValue) || isXmlString(itemValue))) {
    return (
      <>
        <span className="flex justify-between text-sm font-normal capitalize text-foundations-subtle">
          <span>{snakeCaseToHumanReadable(itemKey)}</span>
          <CopyIconButton text={itemValue} />
        </span>
        <JsonPreview itemValue={itemValue} />
      </>
    );
  }

  if (itemValue && typeof itemValue === "object") {
    return (
      <>
        <span className="flex justify-between text-sm font-normal capitalize text-foundations-subtle">
          <span>{snakeCaseToHumanReadable(itemKey)}</span>
          <CopyIconButton text={JSON.stringify(itemValue)} />
        </span>
        <JsonPreview itemValue={itemValue as Record<string, string>} />
      </>
    );
  }

  if (isString(itemValue) && itemValue?.includes?.("http")) {
    return (
      <>
        <span className="flex justify-between text-sm font-normal capitalize text-foundations-subtle">
          <span>{snakeCaseToHumanReadable(itemKey)}</span>
          <CopyIconButton text={itemValue} />
        </span>
        <a
          className="break-all text-sm font-normal text-foundations-bolder hover:text-info"
          target="_blank"
          rel="noreferrer"
          href={itemValue}
        >
          {itemValue} <ExternalLinkIcon className="inline h-4 w-4" />
        </a>
      </>
    );
  }

  if (
    typeof itemValue === "boolean" &&
    ["true", "false"].includes(itemValue?.toString())
  ) {
    return (
      <>
        <span className="text-sm font-normal capitalize text-foundations-subtle">
          {snakeCaseToHumanReadable(itemKey)}
        </span>
        <div className="text-sm font-normal text-foundations-bolder">
          <BooleanBadge value={itemValue?.toString() === "true"} />
        </div>
      </>
    );
  }

  return (
    <>
      <span className="text-sm font-normal capitalize text-foundations-subtle">
        {snakeCaseToHumanReadable(itemKey)}
      </span>
      <p className="text-sm font-normal text-foundations-bolder">
        {itemValue?.toString()}
      </p>
    </>
  );
}
